import * as actions from '../actions/'
import ExposureChart, { Props } from '../components/ExposureChart'
import {
  ClientExposuresTransactions,
  LimitType,
  AlertLevels,
  Model,
} from '../types/types'

import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import { updateClientMetricMuted } from '../actions/'
import { GUID } from '../guids'
import * as result from '../result'
import {
  defaultMetricLevels,
  defaultMetricMutes,
  toGuidLookupTable,
} from '../utils'
import {ClientCode, ClientConfig} from "../types/clients";
import {hasCodeDecoder} from "../web/decoders";

type ContainerDispatchProps = Pick<
  Props,
  'clearSelection' | 'onClientSelectorClick' | 'toggleMute'
>
type ContainerStateProps = Pick<
  Props,
  | 'activeClientInfo'
  | 'clients'
  | 'guidLookup'
  | 'metricLevels'
  | 'selectedClientCode'
>

export function mapStateToProps({
  clients,
  clientSettings,
  metricLevels,
  mutedClientMetrics,
  selectedClientCode,
}: Model): ContainerStateProps {
  const guidLookup: Record<ClientCode, GUID | undefined> = clientSettings.reduce(toGuidLookupTable, {})

  return {
    activeClientInfo: collectClientInfo({
      clientSettings,
      clients,
      metricLevels,
      mutedClientMetrics,
      selectedClientCode,
    }),
    clients,
    guidLookup,
    metricLevels,
    selectedClientCode,
  }
}

export function mapDispatchToProps(
  dispatch: Dispatch<actions.RootAction>,
): ContainerDispatchProps {
  return {
    clearSelection: (): void => {
      dispatch(actions.selectClient(undefined))
    },
    // Using `unknown` here as Recharts has extremely loose typing for this
    onClientSelectorClick: (propsFromRecharts: unknown): void => {
      const { code } = hasCodeDecoder.runWithException(propsFromRecharts)
      dispatch(actions.selectClient(code))
    },

    toggleMute: (
      clientCode: ClientCode,
      metric: LimitType,
    ): ReturnType<Props['toggleMute']> => (): void => {
      dispatch(updateClientMetricMuted(clientCode, metric, 'toggle'))
    },
  }
}

function mergeProps(
  stateProps: ContainerStateProps,
  dispatchProps: ContainerDispatchProps,
  ownProps: {}, // Is this the correct type???
): ContainerStateProps & ContainerDispatchProps {
  return { ...ownProps, ...stateProps, ...dispatchProps }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(ExposureChart)

function collectClientInfo({
  clients,
  clientSettings,
  metricLevels,
  mutedClientMetrics,
  selectedClientCode,
}: Pick<
  Model,
  | 'clients'
  | 'clientSettings'
  | 'metricLevels'
  | 'selectedClientCode'
  | 'mutedClientMetrics'
>): Props['activeClientInfo'] {
  if (selectedClientCode === undefined) {
    return result.err('no client selected')
  }
  const values: ClientExposuresTransactions | undefined = clients.find(
    eachClient => eachClient.code === selectedClientCode,
  )
  if (values === undefined) {
    return result.err('no data for given client')
  }

  const metrics: AlertLevels | undefined = metricLevels[selectedClientCode]

  const clientSetting: ClientConfig | undefined = clientSettings.find((c) => c.code === selectedClientCode)

  if (clientSetting === undefined) {
    return result.err('no settings found for given client')
  }

  const mutedMetrics =
    mutedClientMetrics[clientSetting.code] || defaultMetricMutes

  return result.ok({
    metricLevels: metrics || defaultMetricLevels,
    mutedMetrics,
    state: clientSetting,
    values,
  })
}
